import { Alert, Button, Modal, useToast } from "@nabble/ui";
import { type FormEvent, useState } from "react";
import { addMember, type AssignableRole, type ManagedOrganization } from "../../../api/organizations";
import { NewPersonFields } from "../../../components/NewPersonFields";
import { RolePicker } from "../../../components/RolePicker";
import { errorMessage } from "../../../errors";
import { type NewPerson, type SubmitFailure, useNewPersonForm } from "../../../hooks/useNewPersonForm";
import { DEFAULT_ROLE_IDS } from "../../../model/organizationRoles";

interface AddPersonDialogProps {
  organization: ManagedOrganization;
  roles: AssignableRole[];
  onClose: () => void;
  onAdded: () => void;
}

function explainFailure(err: unknown): SubmitFailure {
  return { field: "form", text: errorMessage(err, "We couldn't add them.") };
}

/** Adds someone to the organization by email: they join at once or get an invitation. */
export function AddPersonDialog({ organization, roles, onClose, onAdded }: AddPersonDialogProps): React.JSX.Element {
  const { show } = useToast();
  const form = useNewPersonForm();
  const [roleIds, setRoleIds] = useState<string[]>([...DEFAULT_ROLE_IDS]);

  async function send(person: NewPerson): Promise<void> {
    await addMember(organization.id, { ...person, roleIds });
    show({
      title: "Person added",
      text: `If ${person.email} didn't have an account yet, we've emailed them an invitation.`,
      tone: "success",
    });
    onAdded();
  }

  const submit = (e?: FormEvent): Promise<void> => form.submit(e, send, explainFailure);

  return (
    <Modal
      open
      icon="userPlus"
      title={`Add a person to ${organization.name}`}
      description="Someone who already uses Nabble Suite joins at once. Anyone else gets an email to create their account."
      onClose={onClose}
      actions={
        <>
          <Button label="Cancel" variant="secondary" onClick={onClose} />
          <Button label="Add person" icon="send" loading={form.loading} disabled={!form.filled || roleIds.length === 0} onClick={() => void submit()} />
        </>
      }
    >
      <form className="nb-stack" onSubmit={(e) => void submit(e)} noValidate>
        <NewPersonFields form={form} />
        <RolePicker roles={roles} value={roleIds} onChange={setRoleIds} showLabel />
        {form.error ? <Alert tone="danger">{form.error}</Alert> : null}
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
